import { type ReactNode, type Ref } from "react";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { Kpi } from "@acto/taskpane/core/domain";
import { useNumberFormatter } from "@acto/taskpane/core/utils/hooks/useNumberFormatter.tsx";
import { useOpenCustomer } from "@acto/taskpane/field-sales-2/navigation";
import { SearchHeaderRow } from "@acto/taskpane/field-sales-2/components/patterns/Search";
import { Card, RowMeta, RowTitle } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { CustomerRevenueCard } from "@acto/taskpane/field-sales-2/components/primitives/CustomerRevenueCard";
import { LoadMoreFooter } from "@acto/taskpane/field-sales-2/components/primitives/LoadMoreFooter";
import {
  ProductTrendList,
  type ProductTrendItem,
} from "@acto/taskpane/field-sales-2/components/primitives/ProductTrendList";
import type { ProductTrendFilter } from "@acto/gql/graphql";
import type { KpiTrend } from "@acto/taskpane/field-sales-2/data-access/useKpiTrend";
import { useGroupMembers } from "@acto/taskpane/field-sales-2/data-access/useGroupMembers";
import { useGroupOthers, type GroupOthersAggregate } from "@acto/taskpane/field-sales-2/data-access/useGroupOthers";
import { toCustomerTrendItem } from "@acto/taskpane/field-sales-2/data-access/customerTrendItem";
import { formatKpiValue } from "@acto/taskpane/field-sales-2/utils/format";
import { FilterHeaderRow } from "./FilterHeaderRow";
import { toClientTrendsVariables } from "./productQuickFilter";
import { CurrencyCode } from "@acto/taskpane/core/utils/currencyCode";

const OthersRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[2],
}));

interface GroupMembersViewProps {
  /** A member client of the group; scopes all member queries. */
  clientId: string;
  groupTitle: string;
  metaLabel: string;
  selectedKpi: Kpi;
  trend: KpiTrend;
  titleSlot: ReactNode;
  dateRange: { startDate: string; endDate: string };
  searchValue: string;
  debouncedSearch: string;
  onSearchChange: (value: string) => void;
  trends?: ProductTrendFilter[];
  filterSlot?: ReactNode;
  /** Marks the top of the header so the page can reveal the nav dock once it scrolls out of view. */
  headerSentinelRef?: Ref<HTMLDivElement>;
}

/**
 * The group's KPI card plus the ranked member list, with an aggregate row for the members
 * the viewer cannot access.
 */
export function GroupMembersView({
  clientId,
  groupTitle,
  metaLabel,
  selectedKpi,
  trend,
  titleSlot,
  dateRange,
  searchValue,
  debouncedSearch,
  onSearchChange,
  trends,
  filterSlot,
  headerSentinelRef,
}: GroupMembersViewProps) {
  const t = useTranslations("fieldSalesTwo");
  const tGroup = useTranslations("fieldSalesTwo.corporateGroup");
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const formatNumber = useNumberFormatter();
  const openCustomer = useOpenCustomer();

  const clientTrends = toClientTrendsVariables(trends);
  const members = useGroupMembers({
    clientId,
    selectedKpi,
    dateRange,
    searchTerm: debouncedSearch,
    trends: clientTrends,
  });
  const { others } = useGroupOthers(clientId, dateRange);

  const items = (members.data ?? []).map((member) =>
    toCustomerTrendItem(member, selectedKpi, locale, currency, formatNumber),
  );

  const isFirstLoad = members.isFetching && items.length === 0;
  const isLoadingMore = members.isFetching && items.length > 0;
  const isSearchActive = debouncedSearch.trim().length > 0;
  const isTrendFilterActive = !!clientTrends?.length;
  const isEmpty = !isFirstLoad && items.length === 0;

  const handleItemClick = (item: ProductTrendItem) => {
    if (!item.id) return;
    openCustomer(item.id);
  };

  // The others aggregate always spans all inaccessible members; a narrowed (searched or
  // trend-filtered) list would misread it as "the rest of these results", so it hides while
  // either is active.
  const othersRow =
    others && !isSearchActive && !isTrendFilterActive && !isFirstLoad ? (
      <Card>
        <OthersRow>
          <RowTitle>{tGroup("othersRow", { count: others.count })}</RowTitle>
          <RowMeta>{formatOthersValue(others, selectedKpi, locale, currency)}</RowMeta>
        </OthersRow>
      </Card>
    ) : undefined;

  return (
    <>
      <CustomerRevenueCard externalReference={metaLabel} name={groupTitle} titleVariant="small" trend={trend} />
      <ProductTrendList
        items={items}
        onItemClick={handleItemClick}
        isLoading={isFirstLoad}
        emptyLabel={isSearchActive ? t("search.noResults") : tGroup("membersEmpty")}
        footer={
          <>
            {othersRow}
            <LoadMoreFooter
              hasMore={members.hasNextPage}
              isLoadingMore={isLoadingMore}
              onLoadMore={members.fetchNextPage}
            />
          </>
        }
      >
        <FilterHeaderRow filterSlot={filterSlot}>
          <SearchHeaderRow
            title={titleSlot}
            placeholder={t("search.placeholder")}
            value={searchValue}
            onChange={onSearchChange}
            disabled={isEmpty && !isSearchActive}
            bareSearch
          />
        </FilterHeaderRow>
        <div ref={headerSentinelRef} aria-hidden="true" />
      </ProductTrendList>
    </>
  );
}

// Marge leads with the profit sum, matching the member rows; the aggregate carries no margin
// ratio to annotate.
function formatOthersValue(
  others: GroupOthersAggregate,
  selectedKpi: Kpi,
  locale: string,
  currency: CurrencyCode,
): string {
  switch (selectedKpi) {
    case Kpi.PROFIT:
    case Kpi.MARGIN:
      return formatKpiValue(Kpi.PROFIT, others.profit.totalValue, currency, locale);
    case Kpi.ORDERS:
      return formatKpiValue(Kpi.ORDERS, others.orders.totalValue, currency, locale);
    default:
      return formatKpiValue(Kpi.REVENUE, others.revenue.totalValue, currency, locale);
  }
}
